import React, {useEffect, useState} from "react";
import axios from "axios";
import {Link} from "react-router-dom";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {baseUrl, userName} from "../Univ.js";
const getStoredGroups = () => {
    const readGroups = (value) => {
        if (!value) return [];
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed
                    .map((item) => typeof item === "string" ? item : item?.name ?? item?.group_name ?? item?.title ?? "")
                    .map((item) => String(item).trim())
                    .filter(Boolean);
            }
            if (parsed && typeof parsed === "object") {
                const name = parsed.name ?? parsed.group_name ?? parsed.title;
                return name ? [String(name).trim()] : [];
            }
        } catch {
            // Existing localStorage values may be plain strings.
        }
        return String(value)
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    };
    const groups = readGroups(localStorage.getItem("userGroups")) || [];
    const primaryGroup = readGroups(localStorage.getItem("userGroup") || localStorage.getItem("user_group"));
    return [...new Set([...groups, ...primaryGroup])];
};
function ConsView(props) {
    const userGroup = localStorage.getItem("userGroup");
    const userGroups = getStoredGroups();
    const isAdmin = userGroups.some((group) => group.toLowerCase() === "admin");
    const isConsultant = userGroups.some((group) => group.toLowerCase() === "consultant");
    // ============================================================
    // STATE
    // ============================================================
    const [selectedDate, setSelectedDate] = useState(new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000));
    const [opData, setOpData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("waiting");
    const [selectedConsultant, setSelectedConsultant] = useState("");
    // ============================================================
    // LOGGED-IN CONSULTANT
    // ============================================================
    const loggedInConsultant = (localStorage.getItem("username") || localStorage.getItem("userName") || localStorage.getItem("user_name") || userName || "").trim();
    // ============================================================
    // ACTIVE CONSULTANT FILTER
    // ============================================================
    // Consultant filter applies to every logged-in user
    const activeConsultant = selectedConsultant.trim();
    // ============================================================
    // FETCH DATA
    // ============================================================
    const fetchData = async () => {
        setIsLoading(true);
        try {
            // =====================================================
            // FORMAT SELECTED DATE
            // =====================================================
            const formattedDate = selectedDate
                .toISOString()
                .substring(0, 10);
            // =====================================================
            // GET OP APPOINTMENTS
            // =====================================================
            const url = `${baseUrl}op-appointment-view/?start_date=${formattedDate}&end_date=${formattedDate}`;
            const response = await axios.get(url);
            const appointments = Array.isArray(response.data) ? response.data : [];
            // =====================================================
            // GET CONSULT FORM DATA
            // =====================================================
            const appointmentsWithDepartmentData = await Promise.all(appointments.map(async (appointment) => {
                const apptId = appointment?.id;
                // =====================================
                // NO APPOINTMENT ID
                // =====================================
                if (!apptId) {
                    return {
                        ...appointment, diagnosis: "", department_data: {},
                    };
                }
                try {
                    // ================================
                    // CONSULT FORM API
                    // ================================
                    const consultResponse = await axios.get(`${baseUrl}view-consultform/${apptId}`);
                    const consultData = consultResponse?.data;
                    console.log("CONSULT DATA FOR APPOINTMENT:", apptId, consultData);
                    // ================================
                    // NORMALIZE CONSULT DATA
                    // ================================
                    let consultRows = [];
                    if (Array.isArray(consultData)) {
                        consultRows = consultData;
                    } else if (consultData && typeof consultData === "object") {
                        consultRows = [consultData];
                    }
                    // ================================
                    // DIAGNOSIS
                    // ================================
                    const diagnosis = consultRows
                        .map((item) => item?.diagnosis)
                        .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
                        .map((value) => String(value).trim())
                        .join(", ");
                    // ================================
                    // DEPARTMENT DATA
                    // ================================
                    const departmentData = {};
                    consultRows.forEach((item) => {
                        if (!item) {
                            return;
                        }
                        let data = item?.department_data;
                        // -------------------------
                        // NOTHING AVAILABLE
                        // -------------------------
                        if (data === null || data === undefined || data === "") {
                            return;
                        }
                        // -------------------------
                        // JSON STRING
                        // -------------------------
                        if (typeof data === "string") {
                            try {
                                data = JSON.parse(data);
                            } catch (error) {
                                console.log("Could not parse department_data:", data);
                                return;
                            }
                        }
                        // -------------------------
                        // OBJECT
                        // -------------------------
                        if (data && typeof data === "object" && !Array.isArray(data)) {
                            Object.keys(data)
                                .forEach((key) => {
                                    const value = data[key];
                                    if (value !== null && value !== undefined && String(value).trim() !== "") {
                                        departmentData[key] = value;
                                    }
                                });
                        }
                    });
                    // ================================
                    // DEBUG
                    // ================================
                    console.log("MAPPED APPOINTMENT:", {
                        appointmentId: apptId,
                        consultant: appointment?.cons__consultant,
                        diagnosis: diagnosis,
                        department_data: departmentData,
                    });
                    // ================================
                    // RETURN APPOINTMENT DATA
                    // ================================
                    return {
                        ...appointment, diagnosis: diagnosis, department_data: departmentData,
                    };
                } catch (error) {
                    console.error(`Consult form error for appointment ${apptId}:`, error);
                    return {
                        ...appointment, diagnosis: "", department_data: {},
                    };
                }
            }));
            // =====================================================
            // DEBUG
            // =====================================================
            console.log("========================================");
            console.log("FINAL APPOINTMENT DATA:", appointmentsWithDepartmentData);
            console.log("========================================");
            // =====================================================
            // SET GRID DATA
            // =====================================================
            setOpData(appointmentsWithDepartmentData);
            // =====================================================
            // IF CURRENTLY SELECTED CONSULTANT IS NOT AVAILABLE
            // RESET TO ALL
            // =====================================================
            if (
                selectedConsultant &&
                !appointmentsWithDepartmentData.some(
                    (item) =>
                        String(item?.cons__consultant || "").trim().toLowerCase() ===
                        selectedConsultant.trim().toLowerCase()
                )
            ) {
                setSelectedConsultant("");
            }
        } catch (error) {
            console.error("OP APPOINTMENT ERROR:", error);
            setOpData([]);
        } finally {
            setIsLoading(false);
        }
    };
    // ============================================================
    // INITIAL FETCH + AUTO REFRESH
    // ============================================================
    useEffect(() => {
        fetchData();
        const intervalId = setInterval(() => {
            fetchData();
        }, 60000);
        return () => {
            clearInterval(intervalId);
        };
    }, [selectedDate]);
    // ============================================================
    // CONSULTANT LIST
    // ============================================================
    // Automatically creates unique consultant list from OP data.
    // ============================================================
    const consultantData = [...new Set(opData
        .map((item) => item?.cons__consultant)
        .filter((name) => name !== null && name !== undefined && String(name).trim() !== "")
        .map((name) => String(name).trim())),].sort((a, b) => a.localeCompare(b));
    // ============================================================
    // FILTER DATA
    // ============================================================
    // ============================================================
// FILTER DATA
// ============================================================
    const filteredOpData = opData.filter((item) => {
        // Filter by consultant ONLY when Admin selects a dropdown option.
        if (activeConsultant && String(item?.cons__consultant || "").trim().toLowerCase() !== activeConsultant.trim().toLowerCase()) {
            return false;
        }
        const status = Number(item.status);
        if (activeTab === "pending") return status === 1;
        if (activeTab === "waiting") return status === 2;
        if (activeTab === "completed") return status === 3;
        return true;
    });
    // ============================================================
    // TAB COUNTS
    // ============================================================
    // ============================================================
// TAB COUNTS
// ============================================================
// Consultant users see their own counts.
// Everyone else sees counts across all consultants.
    const consultantFilteredData = activeConsultant ? opData.filter((item) => String(item?.cons__consultant || "")
        .trim()
        .toLowerCase() === activeConsultant.toLowerCase()) : opData;
    const pendingCount = consultantFilteredData.filter((item) => Number(item.status) === 1).length;
    const waitingCount = consultantFilteredData.filter((item) => Number(item.status) === 2).length;
    const completedCount = consultantFilteredData.filter((item) => Number(item.status) === 3).length;
    const allCount = consultantFilteredData.length;
    // ============================================================
    // DATAGRID COLUMNS
    // ============================================================
    const columns = [// ========================================================
        // ACTION
        // ========================================================
        {
            field: "action",
            headerName: "Action",
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (<Link
                to={`/cons-detail/${params.row.id}`}
                className="btn btn-success"
            >
                View
            </Link>),
        }, // ========================================================
        // DATE
        // ========================================================
        {
            field: "appt_date", headerName: "Date", width: 110,
        }, // ========================================================
        // APPOINTMENT TIME
        // ========================================================
        {
            field: "appt_time", headerName: "Appt Slot", width: 100,
        }, // ========================================================
        // CONSULTANT
        // ========================================================
        {
            field: "cons__consultant", headerName: "Consultant", width: 160,
        }, // ========================================================
        // APPOINTMENT ID
        // ========================================================
        {
            field: "appt_id", headerName: "Appt Id", width: 120,
        }, // ========================================================
        // PATIENT ID
        // ========================================================
        {
            field: "pat__pat_id", headerName: "Patient ID", width: 110,
        }, // ========================================================
        // TOKEN NUMBER
        // ========================================================
        {
            field: "tokid", headerName: "Token No", width: 80, renderCell: (params) => {
                const tokid = params.value;
                return (<span className="badge bg-primary">

                        {tokid || "-"}

                    </span>);
            },
        }, // ========================================================
        // PATIENT
        // ========================================================
        {
            field: "pat__patient", headerName: "Patient", width: 170,
        }, // ========================================================
        // AGE / SEX
        // ========================================================
        {
            field: "pat__age_gender",
            headerName: "Age/Sex",
            width: 100,
            sortable: false,
            renderCell: (params) => (<span>

                    {params.row.pat__age || "-"}

                {" / "}

                {params.row.pat__gender || "-"}

                </span>),
        }, // ========================================================
        // TYPE
        // ========================================================
        {
            field: "type", headerName: "Type", width: 100, renderCell: (params) => {
                const type = params.value;
                return (<>

                    {type === "new" && (<span className="badge bg-primary">
                                New
                            </span>)}


                    {type === "revisit" && (<span className="badge bg-success">
                                Revisit
                            </span>)}


                    {!["new", "revisit"].includes(type) && (<span className="badge bg-secondary">

                                {type || "-"}

                            </span>)}

                </>);
            },
        }, // ========================================================
        // EXIT TIME
        // ========================================================
        {
            field: "add_time", headerName: "Exit Time", width: 90, renderCell: (params) => {
                if (!params.value) {
                    return "-";
                }
                return String(params.value).split(".")[0];
            },
        }, // ========================================================
        // CHIEF COMPLAINTS
        // ========================================================
        {
            field: "department_data",
            headerName: "Chief Complaints",
            width: 350,
            sortable: false,
            renderCell: (params) => {
                let data = params?.value;
                // -----------------------------------------------
                // JSON STRING
                // -----------------------------------------------
                if (typeof data === "string") {
                    try {
                        data = JSON.parse(data);
                    } catch (error) {
                        data = {};
                    }
                }
                // -----------------------------------------------
                // INVALID DATA
                // -----------------------------------------------
                if (!data || typeof data !== "object" || Array.isArray(data)) {
                    return "-";
                }
                // -----------------------------------------------
                // DISPLAY
                // -----------------------------------------------
                return (<div
                    style={{
                        whiteSpace: "normal", lineHeight: "1.4", padding: "5px 0",
                    }}
                >

                    {data.chief_complaints && (<div>

                        {data.chief_complaints}

                    </div>)}


                    {!data.chief_complaints && (<span>
                                -
                            </span>)}

                </div>);
            },
        }, // ========================================================
        // DIAGNOSIS
        // ========================================================
        {
            field: "diagnosis", headerName: "Diagnosis", width: 180, sortable: false, renderCell: (params) => (<span>

                    {params.row?.diagnosis || ""}

                </span>),
        }, // ========================================================
        // STATUS
        // ========================================================
        {
            field: "status", headerName: "Status", width: 110, renderCell: (params) => {
                const status = Number(params.value);
                return (<>

                    {/* =====================================
                            PENDING
                        ===================================== */}

                    {status === 1 && (<span className="badge bg-primary">

                                Pending

                            </span>)}


                    {/* =====================================
                            WAITING
                        ===================================== */}

                    {status === 2 && (<span className="badge bg-warning text-dark">

                                Waiting

                            </span>)}


                    {/* =====================================
                            COMPLETED
                        ===================================== */}

                    {status === 3 && (<span className="badge bg-success">

                                Completed

                            </span>)}


                    {/* =====================================
                            UNKNOWN
                        ===================================== */}

                    {![1, 2, 3].includes(status) && (<span className="badge bg-secondary">

                                -

                            </span>)}

                </>);
            },
        },];
    // ============================================================
    // DATE CHANGE
    // ============================================================
    const handleDateChange = (date) => {
        if (!date) {
            return;
        }
        const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
        setSelectedDate(localDate);
        // Optional:
        // Reset consultant when changing date
        // Uncomment if desired.
        //
        // setSelectedConsultant("");
    };
    // ============================================================
    // CONSULTANT CHANGE
    // ============================================================
    const handleConsultantChange = (event) => {
        const value = event.target.value;
        setSelectedConsultant(value);
    };
    // ============================================================
    // TAB CHANGE
    // ============================================================
    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };
    // ============================================================
    // RENDER
    // ============================================================
    return (<section className="container mt-4">

        {/* ====================================================
                FILTERS
            ==================================================== */}

        <div className="row">

            {/* ==================================================
                    DATE FILTER
                ================================================== */}

            <div className="col-md-4">

                <div className="mb-4 mt-4">

                    <label
                        htmlFor="selectedDate"
                        className="form-label"
                    >
                        Select Date
                    </label>

                    <DatePicker

                        id="selectedDate"

                        selected={selectedDate}

                        onChange={handleDateChange}

                        dateFormat="dd/MM/yyyy"

                        className="form-control"

                    />

                </div>

            </div>


            {/* ==================================================
                    CONSULTANT FILTER
                    ADMIN ONLY
                ================================================== */}

            <div className="col-md-4">

                <div className="mb-4 mt-4">

                    <label
                        htmlFor="consultant"
                        className="form-label"
                    >
                        Search by Consultant
                    </label>

                    <select

                        name="consultant"

                        id="consultant"

                        value={selectedConsultant}

                        className="form-control"

                        onChange={handleConsultantChange}

                    >

                        <option value="">
                            All Consultants
                        </option>


                        {consultantData.map((consultant, index) => (<option
                            key={index}
                            value={consultant}
                        >
                            {consultant}
                        </option>))}

                    </select>

                </div>

            </div>

        </div>


        {/* ====================================================
                ACTIVE FILTER DISPLAY
            ==================================================== */}

        {selectedConsultant && (<div className="mb-2">

                        <span className="badge bg-info text-dark">

                            Consultant:
                            {" "}
                            {selectedConsultant}

                            <button
                                type="button"
                                className="btn btn-sm ms-2 p-0"
                                onClick={() => setSelectedConsultant("")}
                                style={{
                                    lineHeight: "1",
                                }}
                            >
                                ×
                            </button>

                        </span>

        </div>)}


        {/* ====================================================
                TABS
            ==================================================== */}

        <div className="mt-4">

            <ul className="nav nav-tabs">

                {/* ==================================================
                        WAITING
                    ================================================== */}

                <li className="nav-item">

                    <button

                        type="button"

                        className={`nav-link ${activeTab === "waiting" ? "active" : ""}`}

                        onClick={() => handleTabChange("waiting")}

                    >

                        Waiting

                        <span className="badge bg-warning text-dark ms-2">

                                {waitingCount}

                            </span>

                    </button>

                </li>


                {/* ==================================================
                        PENDING
                    ================================================== */}

                <li className="nav-item">

                    <button

                        type="button"

                        className={`nav-link ${activeTab === "pending" ? "active" : ""}`}

                        onClick={() => handleTabChange("pending")}

                    >

                        Pending

                        <span className="badge bg-primary ms-2">

                                {pendingCount}

                            </span>

                    </button>

                </li>


                {/* ==================================================
                        COMPLETED
                    ================================================== */}

                <li className="nav-item">

                    <button

                        type="button"

                        className={`nav-link ${activeTab === "completed" ? "active" : ""}`}

                        onClick={() => handleTabChange("completed")}

                    >

                        Completed

                        <span className="badge bg-success ms-2">

                                {completedCount}

                            </span>

                    </button>

                </li>


                {/* ==================================================
                        ALL
                    ================================================== */}

                <li className="nav-item">

                    <button

                        type="button"

                        className={`nav-link ${activeTab === "all" ? "active" : ""}`}

                        onClick={() => handleTabChange("all")}

                    >

                        All

                        <span className="badge bg-secondary ms-2">

                                {allCount}

                            </span>

                    </button>

                </li>

            </ul>

        </div>


        {/* ====================================================
                LOADING
            ==================================================== */}

        {isLoading && (<div className="mt-4">

            Loading appointments...

        </div>)}


        {/* ====================================================
                DATA GRID
            ==================================================== */}

        {!isLoading && filteredOpData.length > 0 && (<div

            style={{
                height: "100%", width: "100%",
            }}

            className="mt-4"

        >

            <DataGrid

                className="mt-4"

                sx={{
                    boxShadow: 4, border: 4, borderColor: "primary.light", "& .MuiDataGrid-cell:hover": {
                        color: "primary.main",
                    },
                }}

                rows={filteredOpData}

                columns={columns}

                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 10, page: 0,
                        },
                    },
                }}

                pageSizeOptions={[10, 25, 50, 100,]}

                slots={{
                    toolbar: GridToolbar,
                }}

                autoHeight

            />

        </div>)}


        {/* ====================================================
                NO DATA
            ==================================================== */}

        {!isLoading && filteredOpData.length === 0 && (<div className="mt-4">

            No{" "}

            {activeTab === "pending" ? "pending" : activeTab === "waiting" ? "waiting" : activeTab === "completed" ? "completed" : ""}

            {" "}appointments available
            for the selected date

            {activeConsultant && (<>
                    {" "}for{" "}
                    <strong>{activeConsultant}</strong>
                </>)}
            .

        </div>)}

    </section>);
}
export default ConsView;